import { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import { createTimeReportResponse } from '~/api/timeReportService';
import { IJobCardV2 } from '~/types/models/jobCard';
import { TimeReportUpdateResponse } from '~/types/request/timeReport';
import { jobCardState } from '.';
import {
  createTimeReport,
  fetchJobCardsRunning,
  fetchJobCardsNotRunning,
  updateTimeReport,
  fetchJobCardsRecently,
} from './actions';
import { jobCardAdapter } from './adapter';

export const extraReducers = (builder: ActionReducerMapBuilder<jobCardState>) => {
  /* jobCards Running */
  builder.addCase(fetchJobCardsRunning.pending, (state: jobCardState) => {
    state.jobCardsRunning.loading = true;
  });
  builder.addCase(
    fetchJobCardsRunning.fulfilled,
    (state: jobCardState, { payload }: PayloadAction<{ list: IJobCardV2[]; totalCount?: number }>) => {
      jobCardAdapter.addMany(state.jobCardsRunning, payload.list);
      state.jobCardsRunning.loading = false;
      state.jobCardsRunning.pagination.all = payload.totalCount || state.jobCardsRunning.pagination.all;
      state.jobCardsRunning.pagination.isMax = payload.list.length < state.jobCardsRunning.pagination.count;
      state.jobCardsRunning.pagination.page++;
    },
  );
  builder.addCase(fetchJobCardsRunning.rejected, (state: jobCardState) => {
    state.jobCardsRunning.loading = false;
  });
  /* jobCards Not Running */
  builder.addCase(fetchJobCardsNotRunning.pending, (state: jobCardState) => {
    state.jobCardsNotRunning.loading = true;
  });
  builder.addCase(
    fetchJobCardsNotRunning.fulfilled,
    (state: jobCardState, { payload }: PayloadAction<{ list: IJobCardV2[]; totalCount?: number }>) => {
      jobCardAdapter.addMany(state.jobCardsNotRunning, payload.list);
      state.jobCardsNotRunning.loading = false;
      state.jobCardsNotRunning.pagination.all = payload.totalCount || state.jobCardsNotRunning.pagination.all;
      state.jobCardsNotRunning.pagination.isMax = payload.list.length < state.jobCardsNotRunning.pagination.count;
      state.jobCardsNotRunning.pagination.page++;
    },
  );
  builder.addCase(fetchJobCardsNotRunning.rejected, (state: jobCardState) => {
    state.jobCardsNotRunning.loading = false;
  });
  /* jobCards Recently */
  builder.addCase(fetchJobCardsRecently.pending, (state: jobCardState) => {
    state.jobCardsRecently.loading = true;
  });
  builder.addCase(
    fetchJobCardsRecently.fulfilled,
    (state: jobCardState, { payload }: PayloadAction<{ list: IJobCardV2[]; totalCount?: number }>) => {
      jobCardAdapter.addMany(state.jobCardsRecently, payload.list);
      state.jobCardsRecently.loading = false;
      state.jobCardsRecently.pagination.all = payload.totalCount || state.jobCardsRecently.pagination.all;
      state.jobCardsRecently.pagination.isMax = payload.list.length < state.jobCardsRecently.pagination.count;
      state.jobCardsRecently.pagination.page++;
    },
  );
  builder.addCase(fetchJobCardsRecently.rejected, (state: jobCardState) => {
    state.jobCardsRecently.loading = false;
  });
  /* create Time Report */
  builder.addCase(createTimeReport.pending, (state: jobCardState) => {});
  builder.addCase(
    createTimeReport.fulfilled,
    (state: jobCardState, { payload }: PayloadAction<createTimeReportResponse | undefined>) => {
      if (payload?.result) {
        const { result, stoppedIds = [], stopedTimeReports = [] } = payload;
        const { jobCardId } = result;

        let jobCard = jobCardAdapter.getSelectors().selectById(state.jobCardsNotRunning, jobCardId);

        if (!jobCard) {
          jobCard = jobCardAdapter.getSelectors().selectById(state.jobCardsRunning, jobCardId);
        }

        if (!jobCard) {
          jobCard = jobCardAdapter.getSelectors().selectById(state.jobCardsRecently, jobCardId);
        }

        if (!jobCard) {
          return;
        }

        const updatedReports = (jobCard?.Reports || []).map((report) => {
          if (stoppedIds.includes(report._id)) {
            return {
              ...report,
              reportEndDateTime: result.reportStartDateTime,
            };
          } else {
            return report;
          }
        });

        const changes = {
          Reports: [...updatedReports, result],
        };

        if (state.selectedJobCard) {
          state.selectedJobCard.Reports = changes.Reports;
        }

        const runningJobCards = jobCardAdapter.getSelectors().selectAll(state.jobCardsRunning);

        if (!result.reportEndDateTime && !runningJobCards.find((card) => card._id === result.jobCardId)) {
          state.jobCardsRunning.pagination.all = state.jobCardsRunning.pagination.all + 1;
          state.jobCardsNotRunning.pagination.all = state.jobCardsNotRunning.pagination.all - 1;
          jobCardAdapter.addOne(state.jobCardsRunning, jobCard);
          jobCardAdapter.removeOne(state.jobCardsNotRunning, jobCardId);
        }

        jobCardAdapter.updateOne(state.jobCardsNotRunning, { id: jobCardId, changes });
        jobCardAdapter.updateOne(state.jobCardsRunning, { id: jobCardId, changes });
        jobCardAdapter.updateOne(state.jobCardsRecently, { id: jobCardId, changes });

        if (result.reportEndDateTime) {
          state.isAddedReportWithEndTime = true;
        }

        if (stopedTimeReports.length !== 0) {
          const reportsForStop = stopedTimeReports.filter((item) => item.dmsJCEntry !== result.dmsJCEntry);
          reportsForStop.forEach((stoppedReport) => {
            const jobCard = jobCardAdapter.getSelectors().selectById(state.jobCardsRunning, stoppedReport.jobCardId);
            jobCardAdapter.removeOne(state.jobCardsRunning, stoppedReport.jobCardId);
            if (jobCard) {
              const reports = jobCard.Reports.map((report) =>
                report._id === stoppedReport._id ? { ...stoppedReport } : { ...report },
              );

              const entityState = jobCardAdapter.upsertOne(state.jobCardsRecently, {
                ...jobCard,
                Reports: reports,
              });

              state.jobCardsRecently.pagination.all = entityState.ids.length;
            }
            state.jobCardsRunning.pagination.all = state.jobCardsRunning.pagination.all - 1;
          });
        }

        state.showReportSavedModal = true;
      }
    },
  );
  builder.addCase(createTimeReport.rejected, (state: jobCardState) => {
    state.showReportSavedModal = false;
    state.isAddedReportWithEndTime = false;
  });

  /* update Time Report */
  builder.addCase(updateTimeReport.pending, (state: jobCardState) => {
    state.loadingTimeReports = true;
  });
  builder.addCase(
    updateTimeReport.fulfilled,
    (state: jobCardState, { payload }: PayloadAction<TimeReportUpdateResponse>) => {
      if (payload) {
        const { jobCardId, payload: newReportChanges } = payload;

        // let beforeRunning = false;

        let jobCard = jobCardAdapter.getSelectors().selectById(state.jobCardsRunning, jobCardId);

        if (!jobCard) {
          jobCard = jobCardAdapter.getSelectors().selectById(state.jobCardsNotRunning, jobCardId);
          // if (jobCard) {
          //   beforeRunning = true;
          // }
        }

        if (!jobCard) {
          jobCard = jobCardAdapter.getSelectors().selectById(state.jobCardsRecently, jobCardId);
        }

        if (jobCard) {
          const newReports = (jobCard?.Reports || []).map((item) => {
            if (item._id === newReportChanges.id) {
              return {
                ...item,
                ...newReportChanges.changes,
              };
            } else {
              return item;
            }
          });

          const changes = {
            Reports: newReports,
          };

          jobCardAdapter.updateOne(state.jobCardsNotRunning, { id: jobCardId, changes: changes });
          jobCardAdapter.updateOne(state.jobCardsRunning, { id: jobCardId, changes: changes });
          jobCardAdapter.updateOne(state.jobCardsRecently, { id: jobCardId, changes: changes });

          if (state.selectedJobCard) {
            state.selectedJobCard.Reports = changes.Reports;
          }

          if (newReportChanges.changes.reportEndDateTime) {
            const jobCardsRunning = jobCardAdapter.getSelectors().selectAll(state.jobCardsRunning);
            const jobCardsRecently = jobCardAdapter.getSelectors().selectAll(state.jobCardsRecently);

            if (jobCardsRunning.find((card) => card._id === jobCardId)) {
              state.jobCardsRunning.pagination.all = state.jobCardsRunning.pagination.all - 1;
              jobCardAdapter.removeOne(state.jobCardsRunning, jobCardId);
            }

            if (!jobCardsRecently.find((card) => card._id === jobCardId)) {
              state.jobCardsRecently.pagination.all = state.jobCardsRecently.pagination.all + 1;
              jobCardAdapter.addOne(state.jobCardsRecently, { ...jobCard, Reports: changes.Reports });
            }

            // if (beforeRunning) {
            //   // state.jobCardsNotRunning.pagination.all = state.jobCardsNotRunning.pagination.all + 1;
            //   state.jobCardsRunning.pagination.all = state.jobCardsRunning.pagination.all - 1;
            //   state.jobCardsRecently.pagination.all = state.jobCardsRecently.pagination.all + 1;
            //   jobCardAdapter.removeOne(state.jobCardsRunning, jobCardId);
            //   // jobCardAdapter.addOne(state.jobCardsNotRunning, { ...jobCard, Reports: reportChanges.Reports });
            //   jobCardAdapter.addOne(state.jobCardsRecently, { ...jobCard, Reports: changes.Reports });
            // }
          }
        }
      }
      state.loadingTimeReports = false;
    },
  );
  builder.addCase(updateTimeReport.rejected, (state: jobCardState) => {
    state.loadingTimeReports = false;
  });
};
